import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Calendar,
  Building2,
  MapPin,
  Users,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/features/auth/AuthContext'
import type { UserRole } from '@/types/auth'
import { cn } from '@/lib/utils'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { AppSidebar } from './AppSidebar'
import { AppHeader } from './AppHeader'

interface NavItem {
  key: string
  icon: LucideIcon
  path: string
  labelKey: string
  minRoles: UserRole[]
}

const navigationItems: NavItem[] = [
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    path: '/app',
    labelKey: 'navigation.dashboard',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'visits',
    icon: Calendar,
    path: '/app/visits',
    labelKey: 'navigation.visits',
    minRoles: ['gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'companies',
    icon: Building2,
    path: '/app/companies',
    labelKey: 'navigation.companies',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'locations',
    icon: MapPin,
    path: '/app/locations',
    labelKey: 'navigation.locations',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'hosts',
    icon: Users,
    path: '/app/hosts',
    labelKey: 'navigation.hosts',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'settings',
    icon: Settings,
    path: '/app/settings',
    labelKey: 'navigation.settings',
    minRoles: ['tenant_admin', 'super_admin'],
  },
]

export function AppLayout() {
  const { t } = useTranslation()
  const { hasRole } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  const visibleItems = navigationItems.filter((item) => hasRole(item.minRoles))

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <AppSidebar isOpen={sidebarOpen} />

      {/* Mobile sidebar */}
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent side="left" className="w-60 p-0">
          <SheetHeader className="border-b h-16 flex items-center px-4">
            <SheetTitle className="text-lg font-bold">{t('app.name')}</SheetTitle>
          </SheetHeader>
          <nav className="p-2 space-y-1">
            {visibleItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.path}
                end={item.path === '/app'}
                onClick={() => setMobileSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span>{t(item.labelKey)}</span>
              </NavLink>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <AppHeader
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
        />
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
